import { StanButton } from '@/components';
import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { cn } from '@/lib';
import { Themes as themes } from '../../../constants/themeConstants';

import { CustomizationsConfiguration, Fonts, SecondaryFonts } from '@/features/theme-editor/types/store';
import { StoreTheme } from '@/types/store';
import { isEqual } from 'lodash';
import { HTMLAttributes, useState } from 'react';
import { tv } from 'tailwind-variants';
import { Panel, PanelFooter } from '../../Panel';
import { Icon } from '../../ui/Icon';

interface Theme extends HTMLAttributes<HTMLButtonElement> {
    id: StoreTheme;
    name: string;
    preview: string;
    active?: boolean;
    current?: boolean;
}

export const ThemeItem = ({ name, preview, active = false, current = false, ...rest }: Theme) => {
    const variants = tv({
        base: 'text-lg font-semibold flex flex-col h-full w-full cursor-pointer space-y-3 hover:bg-bg-color-subtle text-left animate-in fade-in fill-mode-backwards slide-in-from-bottom-[100px] duration-400 ease-out-circ border-neutral-subtle rounded-md border p-3 transition-colors',
        variants: {
            active: {
                true: 'border-border-brand bg-global-grey-10-stan',
            },
            current: {
                true: 'border-border-brand bg-global-grey-10-stan',
            },
        },
    });

    return (
        <button type='button' className={cn(variants({ active, current }), 'group')} {...rest}>
            <span className='block h-full w-full overflow-hidden rounded-sm'>
                <img
                    src={preview}
                    alt={name}
                    className={cn(
                        'h-[200%] w-full object-cover object-top transition-transform duration-2000 ease-out',
                        'translate-y-0 group-hover:-translate-y-[50%]'
                    )}
                />
            </span>
            <span className='animate-in fade-in fill-mode-backwards slide-in-from-bottom ease-out-circ flex flex-shrink-0 items-center justify-start gap-2 duration-600'>
                {name}
                <span
                    className={cn(
                        'text-stan-950 bg-brand-subtle rounded-full px-3 py-2 text-sm',
                        active ? 'opacity-100' : 'opacity-0',
                        'transition-opacity duration-100'
                    )}
                >
                    Active
                </span>
            </span>
        </button>
    );
};

export const Themes = () => {
    const { theme: storeTheme, updateStoreCustomizations, cachedState } = useCustomizationStore();
    const { theme, colors, primaryFont, secondaryFont, imageFrames } = cachedState ?? {};

    const [cachedCustomizations] = useState<Partial<CustomizationsConfiguration> | null>({
        theme: theme ?? undefined,
        colors: colors ?? undefined,
        fonts: {
            primary: primaryFont as Fonts,
            secondary: secondaryFont as SecondaryFonts,
        },
        imageFrames: imageFrames ?? undefined,
    });

    const resetThemes = () => {
        updateStoreCustomizations({ ...cachedCustomizations });
    };

    const isDirty = !isEqual(cachedCustomizations?.theme, storeTheme);

    return (
        <Panel
            footer={
                <>
                    {isDirty && (
                        <PanelFooter completeBtnLabel='Apply'>
                            <div className='absolute left-1/2 -translate-x-1/2'>
                                <StanButton
                                    name='revert'
                                    onClick={resetThemes}
                                    color='secondary'
                                    variant='outline'
                                    size='sm'
                                    className='aspect-square !h-auto w-10 !min-w-0 !p-0'
                                    icon={<Icon name='revert' className='relative left-0.5' />}
                                />
                            </div>
                        </PanelFooter>
                    )}
                </>
            }
        >
            <ul className='text-semantic-fg-base space-y-8 text-lg font-semibold'>
                {themes.map((theme, index) => (
                    <li key={theme.id} className='h-80 overflow-hidden'>
                        <ThemeItem
                            id={theme.id}
                            name={theme.name}
                            preview={theme.preview}
                            key={theme.id}
                            active={theme.id === cachedCustomizations?.theme}
                            current={theme.id === storeTheme}
                            onClick={() => updateStoreCustomizations({ theme: theme.id })}
                            style={{ animationDelay: `${index * 0.05}s` }}
                        />
                    </li>
                ))}
            </ul>
        </Panel>
    );
};
